import { useFetcher } from '@remix-run/react'
import React from 'react'

type FetcherData<T> = NonNullable<T>
type ResolveFunction<T> = (value: FetcherData<T>) => void

export function useFetcherWithPromise<T>() {
  const fetcher = useFetcher<T>()
  const resolveRef = React.useRef<ResolveFunction<T>>()
  const promiseRef = React.useRef<Promise<FetcherData<T>>>()

  if (!promiseRef.current) {
    promiseRef.current = new Promise<FetcherData<T>>((resolve) => {
      resolveRef.current = resolve
    })
  }

  const resetResolver = React.useCallback(() => {
    promiseRef.current = new Promise((resolve) => {
      resolveRef.current = resolve
    })
  }, [promiseRef, resolveRef])

  const submit = React.useCallback(
    async (...args: Parameters<typeof fetcher.submit>) => {
      fetcher.submit(...args)
      return promiseRef.current
    },
    [fetcher, promiseRef]
  )

  const load = React.useCallback((...args: Parameters<typeof fetcher.load>) => {
    fetcher.load(...args)
    return promiseRef.current
  }, [fetcher, promiseRef])

  React.useEffect(() => {
    if (fetcher.state === 'idle') {
      if (fetcher.data) {
        resolveRef.current?.(fetcher.data as FetcherData<T>)
      }
      resetResolver()
    }
  }, [fetcher, resetResolver])

  return { ...fetcher, submit, load }
}